
import { useState, useEffect, useContext } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { usStates } from "@/utils/states";
import { useBuyer } from "@/contexts/BuyerContext";

// Location parsing types and helper
interface ParsedLocation {
  type: 'city_state' | 'state' | 'generic';
  city?: string;
  stateAbbr?: string;
  stateName?: string;
  term?: string;
}

const parseLocation = (input: string): ParsedLocation | null => {
  const sanitized = input.toLowerCase().trim().replace(/[%_'"\\;]/g, '').substring(0, 100);
  
  if (!sanitized) return null;
  
  // Check if input contains a comma (City, State format)
  if (sanitized.includes(',')) {
    const parts = sanitized.split(',').map(p => p.trim()).filter(p => p);
    if (parts.length >= 2) {
      const cityPart = parts[0];
      const statePart = parts[1];
      
      // Check if the second part is a valid state
      const matchedState = usStates.find(state => 
        state.name.toLowerCase() === statePart || 
        state.id.toLowerCase() === statePart
      );
      
      if (matchedState) {
        return { 
          type: 'city_state', 
          city: cityPart, 
          stateAbbr: matchedState.id,
          stateName: matchedState.name 
        };
      }
    }
  }
  
  // Check if it's just a state name/abbreviation
  const matchedState = usStates.find(state => 
    state.name.toLowerCase() === sanitized || 
    state.id.toLowerCase() === sanitized
  );
  
  if (matchedState) {
    return { type: 'state', stateAbbr: matchedState.id, stateName: matchedState.name };
  }
  
  // Generic search term
  return { type: 'generic', term: sanitized };
};


interface SearchFilters {
  location: string;
  cities: string;
  states: string;
  zipCodes: string;
  minPrice: string;
  maxPrice: string;
  bedrooms: string;
  bathrooms: string;
  propertyType: string;
  minArv: string;
  maxArv: string;
  status: string;
}

interface Property {
  id: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  image_url: string | null;
  status: string;
  created_at: string;
  arv_value: number | null;
  property_type: string;
  closed_date: string | null;
  contract_expiration_date: string | null;
  isPreferredMarket?: boolean;
}

export const usePropertySearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isBuyerRoute = location.pathname.startsWith('/buyer');
  
  // Only use buyer context on buyer routes to avoid context errors
  let buyerProfile = null;
  try {
    if (isBuyerRoute) {
      const buyerContext = useBuyer();
      buyerProfile = buyerContext.buyerProfile;
    }
  } catch (e) {
    // Context not available, continue without buyer personalization
  }
  
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    location: "",
    cities: "",
    states: "",
    zipCodes: "",
    minPrice: "",
    maxPrice: "",
    bedrooms: "any",
    bathrooms: "any",
    propertyType: "any",
    minArv: "",
    maxArv: "",
    status: "active"
  });
  
  const [properties, setProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [resultsTruncated, setResultsTruncated] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Load search criteria from URL parameters (for saved searches)
  useEffect(() => {
    const location = searchParams.get('location');
    const cities = searchParams.get('cities');
    const states = searchParams.get('states');
    const zipCodes = searchParams.get('zipCodes');
    const minPrice = searchParams.get('minPrice');
    const maxPrice = searchParams.get('maxPrice');
    const bedrooms = searchParams.get('bedrooms');
    const bathrooms = searchParams.get('bathrooms');
    const propertyType = searchParams.get('propertyType');
    const minArv = searchParams.get('minArv');
    const maxArv = searchParams.get('maxArv');
    const status = searchParams.get('status');

    if (location || cities || states || zipCodes || minPrice || maxPrice || bedrooms || bathrooms || propertyType || minArv || maxArv || status) {
      const newFilters: SearchFilters = {
        location: location || "",
        cities: cities || "",
        states: states || "",
        zipCodes: zipCodes || "",
        minPrice: minPrice || "",
        maxPrice: maxPrice || "",
        bedrooms: bedrooms || "any",
        bathrooms: bathrooms || "any",
        propertyType: propertyType || "any",
        minArv: minArv || "",
        maxArv: maxArv || "",
        status: status || "active"
      };
      
      setSearchFilters(newFilters);
      
      // Auto-execute search if there are parameters
      setTimeout(() => {
        handleSearchWithFilters(newFilters);
      }, 100);
    }
  }, [searchParams]);

  const handleSearchWithFilters = async (filters = searchFilters) => {
    // Check if ANY filter has a non-default value
    const hasAnyFilter = 
      filters.location.trim() !== '' ||
      filters.cities.trim() !== '' ||
      filters.states.trim() !== '' ||
      filters.zipCodes.trim() !== '' ||
      filters.minPrice !== '' ||
      filters.maxPrice !== '' ||
      filters.bedrooms !== 'any' ||
      filters.bathrooms !== 'any' ||
      filters.propertyType !== 'any' ||
      filters.minArv !== '' ||
      filters.maxArv !== '' ||
      filters.status !== 'active';

    if (!hasAnyFilter) {
      toast({
        title: "Search criteria required",
        description: "Please enter at least one search criteria (location, price, status, etc.) to search.",
        variant: "destructive",
      });
      return;
    }

    // Sync filters to URL for back-navigation restoration
    const params: Record<string, string> = {};
    if (filters.location.trim()) params.location = filters.location.trim();
    if (filters.cities.trim()) params.cities = filters.cities.trim();
    if (filters.states.trim()) params.states = filters.states.trim();
    if (filters.zipCodes.trim()) params.zipCodes = filters.zipCodes.trim();
    if (filters.minPrice) params.minPrice = filters.minPrice;
    if (filters.maxPrice) params.maxPrice = filters.maxPrice;
    if (filters.bedrooms && filters.bedrooms !== 'any') params.bedrooms = filters.bedrooms;
    if (filters.bathrooms && filters.bathrooms !== 'any') params.bathrooms = filters.bathrooms;
    if (filters.propertyType && filters.propertyType !== 'any') params.propertyType = filters.propertyType;
    if (filters.minArv) params.minArv = filters.minArv;
    if (filters.maxArv) params.maxArv = filters.maxArv;
    if (filters.status && filters.status !== 'active') params.status = filters.status;
    setSearchParams(params, { replace: true });

    setIsLoading(true);
    setHasSearched(true);
    
    try {
      let query = supabase
        .from('properties')
        .select('*');

      // Status filter with expiration date handling
      const todayStr = new Date().toISOString().split('T')[0];
      
      if (filters.status === 'all') {
        // User explicitly wants ALL statuses
        query = query.in('status', ['active', 'pending', 'sold', 'expired']);
      } else if (filters.status === 'active') {
        // Active properties must not be expired (expiration date must be in the future, not today)
        query = query
          .eq('status', 'active')
          .or(`contract_expiration_date.is.null,contract_expiration_date.gt.${todayStr}`);
      } else if (filters.status === 'pending') {
        // Pending properties must not be expired
        query = query
          .eq('status', 'pending')
          .or(`contract_expiration_date.is.null,contract_expiration_date.gt.${todayStr}`);
      } else {
        // Show only the selected status (sold, etc.)
        query = query.eq('status', filters.status);
      }

      // Enhanced location filter to handle "City, State" format
      if (filters.location) {
        const parsed = parseLocation(filters.location);
        
        if (parsed) {
          if (parsed.type === 'city_state') {
            // City AND State must match - filter by city first, then state
            query = query
              .ilike('city', `%${parsed.city}%`)
              .or(`state.ilike.%${parsed.stateAbbr}%,state.ilike.%${parsed.stateName}%`);
          } else if (parsed.type === 'state') {
            // State-only search
            query = query.or(`state.ilike.%${parsed.stateAbbr}%,state.ilike.%${parsed.stateName}%`);
          } else if (parsed.term) {
            // Generic search across address, city, state, zip
            query = query.or(`address.ilike.%${parsed.term}%,city.ilike.%${parsed.term}%,state.ilike.%${parsed.term}%,zip_code.ilike.%${parsed.term}%`);
          }
        }
      }

      // Multi-city filter (comma-separated)
      if (filters.cities && filters.cities.trim()) {
        const cityList = filters.cities
          .split(',')
          .map(c => c.trim().toLowerCase().replace(/[%_'"\\;]/g, ''))
          .filter(c => c.length > 0);
        
        if (cityList.length > 0) {
          const cityConditions = cityList.map(city => `city.ilike.%${city}%`).join(',');
          query = query.or(cityConditions);
        }
      }

      // Multi-state filter (comma-separated)
      if (filters.states && filters.states.trim()) {
        const stateInputs = filters.states
          .split(',')
          .map(s => s.trim().toLowerCase().replace(/[%_'"\\;]/g, ''))
          .filter(s => s.length > 0);
        
        if (stateInputs.length > 0) {
          const stateConditions: string[] = [];
          stateInputs.forEach(input => {
            const matched = usStates.find(s => 
              s.id.toLowerCase() === input || 
              s.name.toLowerCase() === input
            );
            if (matched) {
              stateConditions.push(`state.ilike.%${matched.id}%`);
              stateConditions.push(`state.ilike.%${matched.name}%`);
            } else {
              stateConditions.push(`state.ilike.%${input}%`);
            }
          });
          query = query.or(stateConditions.join(','));
        }
      }

      // Multi-zip filter (comma-separated, exact match)
      if (filters.zipCodes && filters.zipCodes.trim()) {
        const zipList = filters.zipCodes
          .split(',')
          .map(z => z.trim().replace(/[^0-9]/g, ''))
          .filter(z => /^\d{5}$/.test(z));
        
        if (zipList.length > 0) {
          query = query.in('zip_code', zipList);
        }
      }

      // Price filters
      if (filters.minPrice) {
        query = query.gte('price', parseFloat(filters.minPrice));
      }
      if (filters.maxPrice) {
        query = query.lte('price', parseFloat(filters.maxPrice));
      }

      // Bedrooms filter
      if (filters.bedrooms && filters.bedrooms !== 'any') {
        query = query.gte('bedrooms', parseInt(filters.bedrooms));
      }

      // Bathrooms filter
      if (filters.bathrooms && filters.bathrooms !== 'any') {
        query = query.gte('bathrooms', parseFloat(filters.bathrooms));
      }

      // Property type filter
      if (filters.propertyType && filters.propertyType !== 'any') {
        query = query.eq('property_type', filters.propertyType);
      }

      // ARV filters
      if (filters.minArv) {
        query = query.gte('arv_value', parseFloat(filters.minArv));
      }
      if (filters.maxArv) {
        query = query.lte('arv_value', parseFloat(filters.maxArv));
      }

      // First get total count
      const countQuery = supabase
        .from('properties')
        .select('*', { count: 'exact', head: true });

      // Apply same filters for count (including expiration date handling)
      if (filters.status === 'all') {
        countQuery.in('status', ['active', 'pending', 'sold', 'expired']);
      } else if (filters.status === 'active') {
        countQuery.eq('status', 'active').or(`contract_expiration_date.is.null,contract_expiration_date.gt.${todayStr}`);
      } else if (filters.status === 'pending') {
        countQuery.eq('status', 'pending').or(`contract_expiration_date.is.null,contract_expiration_date.gt.${todayStr}`);
      } else {
        countQuery.eq('status', filters.status);
      }

      if (filters.location) {
        const parsed = parseLocation(filters.location);
        
        if (parsed) {
          if (parsed.type === 'city_state') {
            countQuery.ilike('city', `%${parsed.city}%`).or(`state.ilike.%${parsed.stateAbbr}%,state.ilike.%${parsed.stateName}%`);
          } else if (parsed.type === 'state') {
            countQuery.or(`state.ilike.%${parsed.stateAbbr}%,state.ilike.%${parsed.stateName}%`);
          } else if (parsed.term) {
            countQuery.or(`address.ilike.%${parsed.term}%,city.ilike.%${parsed.term}%,state.ilike.%${parsed.term}%,zip_code.ilike.%${parsed.term}%`);
          }
        }
      }

      // Multi-city filter for count query
      if (filters.cities && filters.cities.trim()) {
        const cityList = filters.cities
          .split(',')
          .map(c => c.trim().toLowerCase().replace(/[%_'"\\;]/g, ''))
          .filter(c => c.length > 0);
        
        if (cityList.length > 0) {
          const cityConditions = cityList.map(city => `city.ilike.%${city}%`).join(',');
          countQuery.or(cityConditions);
        }
      }

      // Multi-state filter for count query
      if (filters.states && filters.states.trim()) {
        const stateInputs = filters.states
          .split(',')
          .map(s => s.trim().toLowerCase().replace(/[%_'"\\;]/g, ''))
          .filter(s => s.length > 0);
        
        if (stateInputs.length > 0) {
          const stateConditions: string[] = [];
          stateInputs.forEach(input => {
            const matched = usStates.find(s => 
              s.id.toLowerCase() === input || 
              s.name.toLowerCase() === input
            );
            if (matched) {
              stateConditions.push(`state.ilike.%${matched.id}%`);
              stateConditions.push(`state.ilike.%${matched.name}%`);
            } else {
              stateConditions.push(`state.ilike.%${input}%`);
            }
          });
          countQuery.or(stateConditions.join(','));
        }
      }

      // Multi-zip filter for count query
      if (filters.zipCodes && filters.zipCodes.trim()) {
        const zipList = filters.zipCodes
          .split(',')
          .map(z => z.trim().replace(/[^0-9]/g, ''))
          .filter(z => /^\d{5}$/.test(z));
        
        if (zipList.length > 0) {
          countQuery.in('zip_code', zipList);
        }
      }

      if (filters.minPrice) countQuery.gte('price', parseFloat(filters.minPrice));
      if (filters.maxPrice) countQuery.lte('price', parseFloat(filters.maxPrice));
      if (filters.bedrooms && filters.bedrooms !== 'any') countQuery.gte('bedrooms', parseInt(filters.bedrooms));
      if (filters.bathrooms && filters.bathrooms !== 'any') countQuery.gte('bathrooms', parseFloat(filters.bathrooms));
      if (filters.propertyType && filters.propertyType !== 'any') countQuery.eq('property_type', filters.propertyType);
      if (filters.minArv) countQuery.gte('arv_value', parseFloat(filters.minArv));
      if (filters.maxArv) countQuery.lte('arv_value', parseFloat(filters.maxArv));

      const { count } = await countQuery;
      const total = count || 0;
      setTotalCount(total);
      setResultsTruncated(total > 500);

      // Now get actual data with limit
      const { data, error } = await query.order('created_at', { ascending: false }).limit(500);

      if (error) {
        console.error('Search error:', error);
        toast({
          title: "Search failed",
          description: "Unable to search properties. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Apply buyer preferred market sorting if available
      let sortedData: Property[] = (data || []).map(p => ({
        id: p.id,
        address: p.address,
        city: p.city,
        state: p.state,
        zip_code: p.zip_code,
        price: p.price,
        bedrooms: p.bedrooms,
        bathrooms: p.bathrooms,
        square_feet: p.square_feet,
        image_url: p.image_url,
        status: p.status,
        created_at: p.created_at || '',
        arv_value: p.arv_value,
        property_type: p.property_type,
        closed_date: p.closed_date,
        contract_expiration_date: p.contract_expiration_date,
        isPreferredMarket: false
      }));

      if (isBuyerRoute && buyerProfile?.preferred_markets && Array.isArray(buyerProfile.preferred_markets)) {
        const preferredMarkets = buyerProfile.preferred_markets as string[];
        const preferredStates = preferredMarkets
          .map(market => {
            const parts = market.split(',').map(p => p.trim());
            return parts[parts.length - 1]; // Get state part
          })
          .filter(Boolean);

        sortedData = sortedData.map(property => ({
          ...property,
          isPreferredMarket: preferredStates.some(state => 
            property.state.toLowerCase().includes(state.toLowerCase()) ||
            state.toLowerCase().includes(property.state.toLowerCase())
          )
        }));

        // Sort: preferred markets first, then by created_at
        sortedData.sort((a, b) => {
          if (a.isPreferredMarket && !b.isPreferredMarket) return -1;
          if (!a.isPreferredMarket && b.isPreferredMarket) return 1;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
      }

      setProperties(sortedData);
      
      if (data?.length === 0) {
        toast({
          title: "No properties found",
          description: "Try adjusting your search criteria to find more properties.",
        });
      } else if (total > 500) {
        toast({
          title: "Results limited",
          description: `Showing 500 of ${total} properties. Refine your search for more specific results.`,
        });
      }
    } catch (error) {
      console.error('Search error:', error);
      toast({
        title: "Search failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchFilters({
      location: "",
      cities: "",
      states: "",
      zipCodes: "",
      minPrice: "",
      maxPrice: "",
      bedrooms: "any",
      bathrooms: "any",
      propertyType: "any",
      minArv: "",
      maxArv: "",
      status: "active"
    });
    setProperties([]);
    setHasSearched(false);
    setResultsTruncated(false);
    setTotalCount(0);
    setSearchParams({}, { replace: true });
  };

  const updateLocation = (location: string) => {
    setSearchFilters(prev => ({ ...prev, location }));
  };

  return {
    searchFilters,
    setSearchFilters,
    properties,
    isLoading,
    hasSearched,
    handleSearchWithFilters,
    clearFilters,
    updateLocation,
    resultsTruncated,
    totalCount
  };
};
